import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { ApiItem, Status, matchesQuery, toStatus } from "../utils/applicationCards";
import {
  ApplicationEditForm,
  ApplicationMenu,
  COLUMNS,
  DEADLINE_TAG,
  DeleteApplicationDialog,
  NewApplicationCard,
  OUTLINE_TAG,
  STATUS_TAG,
  TAG,
  byUpdateDesc,
  columnFor,
  emptyStateFor,
  formatAppDate,
  useApplicationEditor,
} from "../components/applicationShared";
import { deadlineChip, lastUpdate, oaDoneAt, oaDoneChip, stageDeadline } from "../utils/home";
import { cn } from "../lib/utils";

// The List view of the tracker: every application in one table, latest update first,
// with the same stage tags and menu as the board. Also used read-only by the archive view.

const GRID = "grid grid-cols-[minmax(140px,1.3fr)_minmax(180px,2fr)_96px_110px_minmax(140px,1.6fr)_28px] items-center gap-4";

type RowItemProps = {
  item: ApiItem;
  status: Status;
  onUpdate: () => void;
  readOnly: boolean; // archived rows: no menu, so no edit / delete
  onOpen?: (item: ApiItem) => void;
};

function RowItem({ item, status, onUpdate, readOnly, onOpen }: RowItemProps) {
  const editor = useApplicationEditor(item, status, onUpdate);
  const tag = STATUS_TAG[status];
  const deadline = readOnly ? null : stageDeadline(item);
  // An OA the user finished shows that instead of its due date
  const done = readOnly ? null : oaDoneAt(item);

  if (editor.isEditing) {
    return (
      <div className="border-t border-melt-hair bg-white px-4 py-3">
        <ApplicationEditForm editor={editor} wide />
      </div>
    );
  }

  return (
    <>
      <div
        {...(onOpen
          ? {
              role: "button",
              tabIndex: 0,
              "aria-label": `Show emails for ${item.company}`,
              onClick: () => onOpen(item),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onOpen(item);
                }
              },
            }
          : {})}
        className={cn(
          GRID,
          "group border-t border-melt-hair px-4 py-3 transition-colors hover:bg-melt-text1/[.025]",
          onOpen && "cursor-pointer outline-none focus-visible:bg-melt-text1/[.04]"
        )}
      >
        <p className="truncate text-sm font-medium text-melt-text1">{item.company || "Unknown"}</p>

        <div className="min-w-0">
          <p className="truncate text-[13px] text-melt-text2">{item.role || "—"}</p>
          {(item.szn || item.lctn) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {item.szn && <span className={OUTLINE_TAG}>{item.szn}</span>}
              {item.lctn && <span className={OUTLINE_TAG}>{item.lctn}</span>}
            </div>
          )}
        </div>

        <span className="whitespace-nowrap font-plex text-[11px] text-melt-text3">{formatAppDate(lastUpdate(item)) || "—"}</span>

        <span className="flex flex-col items-start gap-1">
          <span className={cn(TAG, tag.className)}>{tag.label}</span>
          {done ? (
            <span className={cn(OUTLINE_TAG, "whitespace-nowrap")}>{oaDoneChip(done)}</span>
          ) : (
            deadline && <span className={cn(DEADLINE_TAG, "whitespace-nowrap")}>{deadlineChip(deadline)}</span>
          )}
        </span>

        <p className="line-clamp-2 whitespace-pre-wrap break-words text-[12.5px] leading-snug text-melt-text2">
          {item.notes || <span className="text-melt-text3">—</span>}
        </p>

        {/* stopPropagation: the ⋯ menu shouldn't also open the emails panel */}
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {!readOnly && (
            <ApplicationMenu
              company={item.company}
              onEdit={editor.startEdit}
              onDelete={() => editor.setShowDelete(true)}
              triggerClassName="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            />
          )}
        </div>
      </div>

      <DeleteApplicationDialog
        open={editor.showDelete}
        onOpenChange={editor.setShowDelete}
        company={item.company}
        onConfirm={editor.remove}
        deleting={editor.deleting}
      />
    </>
  );
}

type RowTrackerProps = {
  data: ApiItem[];
  onUpdate: () => void;
  query?: string;
  /** No + button, row menus or editing (the archive view) */
  readOnly?: boolean;
  /** Clicking a row opens its emails panel. */
  onOpen?: (item: ApiItem) => void;
};

export default function RowTracker({ data, onUpdate, query, readOnly = false, onOpen }: RowTrackerProps) {
  const [adding, setAdding] = useState(false);
  const searching = !!query?.trim();
  // Built during render (not in an effect + state): the filtered list is a
  // new array every render, so an effect depending on it re-ran and re-set
  // state after every render, re-rendering the rows view in a loop.
  const items = useMemo(
    () =>
      data
        .filter((item) => matchesQuery(item, query || "") && columnFor(toStatus(item.predicted_label)))
        .sort(byUpdateDesc),
    [data, query]
  );
  const empty = emptyStateFor(COLUMNS[0], { readOnly, searching });

  return (
    <div className="w-full pb-6 font-grotesk">
      <div className="scrollbar-thin overflow-x-auto rounded-[14px] border border-melt-text1/[.12] bg-melt-paper">
        <div className="min-w-[760px]">
          <div className={cn(GRID, "px-4 py-2.5 font-plex text-[10.5px] font-medium uppercase tracking-[.1em] text-melt-text3")}>
            <span>Company</span>
            <span>Role</span>
            <span>Updated</span>
            <span>Stage</span>
            <span>Notes</span>
            {readOnly ? (
              <span />
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                aria-label="Add application"
                className="grid h-6 w-6 place-items-center justify-self-end rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
              >
                <Plus size={16} strokeWidth={1.75} />
              </button>
            )}
          </div>
          {adding && (
            <NewApplicationCard
              status={COLUMNS[0].addStatus}
              onAdded={onUpdate}
              onClose={() => setAdding(false)}
              wide
              className="rounded-none border-0 border-t border-melt-line shadow-none"
            />
          )}
          {items.length === 0
            ? !adding && (
                <p className="border-t border-melt-hair px-4 py-3.5 text-[13px] text-melt-text3">
                  <span className="font-medium text-melt-text2">{empty.title}</span> · {empty.text}
                </p>
              )
            : items.map((item) => (
                <RowItem
                  key={item.id}
                  item={item}
                  status={toStatus(item.predicted_label) ?? "submitted"}
                  onUpdate={onUpdate}
                  readOnly={readOnly}
                  onOpen={onOpen}
                />
              ))}
        </div>
      </div>
    </div>
  );
}
